import { useState, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = sessionStorage.getItem('gcf-chat-session');
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem('gcf-chat-session', id);
  }
  return id;
};

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  content: "Hey! I'm Ask Cal 👋 Questions about bungee, Aerial Kids, Air Academy or anything GoCalFit? Fire away."
};

const parseSSELine = (line) => {
  if (!line.startsWith('data: ')) return null;
  try {
    return JSON.parse(line.slice(6));
  } catch {
    return null;
  }
};

export const useChat = () => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [isStreaming, setIsStreaming] = useState(false);
  const nextIdRef = useRef(1);

  const appendToLast = useCallback((delta) => {
    setMessages(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, content: last.content + delta };
      return next;
    });
  }, []);

  const replaceLast = useCallback((content) => {
    setMessages(prev => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], content };
      return next;
    });
  }, []);

  const streamResponse = useCallback(async (text) => {
    const response = await fetch(`${API_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: getSessionId(), message: text })
    });
    if (!response.ok || !response.body) throw new Error('bad response');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        const payload = parseSSELine(line);
        if (!payload) continue;
        if (payload.delta) appendToLast(payload.delta);
        else if (payload.error) replaceLast(payload.error);
      }
    }
  }, [appendToLast, replaceLast]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;

    const userId = nextIdRef.current++;
    const botId = nextIdRef.current++;
    setMessages(prev => [
      ...prev,
      { id: `u-${userId}`, role: 'user', content: trimmed },
      { id: `a-${botId}`, role: 'assistant', content: '' }
    ]);
    setIsStreaming(true);

    try {
      await streamResponse(trimmed);
    } catch {
      replaceLast("Hmm, I couldn't connect. Try again in a moment, or email hello@gocalfit.com.");
    } finally {
      setIsStreaming(false);
    }
  }, [isStreaming, streamResponse, replaceLast]);

  return { messages, isStreaming, sendMessage };
};
